import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const sel = "w-full border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-300";

const EMPTY = {
  client_id: "", new_client: false, new_name: "", new_email: "", new_address: "",
  new_zip: "", new_city: "", original_number: "", title: "", issue_date: "",
  validated_date: "", total_ht: "", total_tva: "", total_ttc: "",
  validation_mode: "email", status: "accepte",
};

export default function QuoteImportModal({ open, onClose, onSaved, clients }) {
  const [f, setF] = useState(EMPTY);
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.client_id && !(f.new_client && f.new_name.trim())) {
      toast.error("Sélectionnez un client existant ou saisissez un nouveau client"); return;
    }
    if (!pdf) { toast.error("Joignez le devis original au format PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("client_id", f.new_client ? "" : f.client_id);
      if (f.new_client) {
        fd.append("new_client_name", f.new_name);
        fd.append("new_client_email", f.new_email);
        fd.append("new_client_address", f.new_address);
        fd.append("new_client_zip", f.new_zip);
        fd.append("new_client_city", f.new_city);
      }
      fd.append("original_number", f.original_number);
      fd.append("title", f.title);
      fd.append("issue_date", f.issue_date);
      fd.append("validated_date", f.validated_date);
      fd.append("total_ht", f.total_ht || "0");
      fd.append("total_tva", f.total_tva || "0");
      fd.append("total_ttc", f.total_ttc || "0");
      fd.append("validation_mode", f.validation_mode);
      fd.append("status", f.status);
      fd.append("pdf", pdf);
      await api.post("/accounting/quotes/import", fd);
      toast.success("Devis PDF importé dans la comptabilité");
      setF(EMPTY); setPdf(null); onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'import"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quote-import-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Importer un devis PDF</DialogTitle>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Client associé *</Label>
            <select className={`${sel} mt-1`} value={f.new_client ? "__new__" : f.client_id}
              onChange={(e) => e.target.value === "__new__"
                ? setF({ ...f, new_client: true, client_id: "" })
                : setF({ ...f, new_client: false, client_id: e.target.value })}
              data-testid="import-client">
              <option value="">— Sélectionner un client —</option>
              {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value="__new__">+ Nouveau client</option>
            </select>
          </div>
          {f.new_client && (
            <div className="grid grid-cols-2 gap-3 bg-neutral-50 border border-neutral-200 rounded-lg p-3" data-testid="import-new-client">
              <div className="col-span-2"><Label className="text-xs">Nom du client *</Label>
                <Input className="mt-1" value={f.new_name} onChange={set("new_name")} data-testid="import-new-name" /></div>
              <div><Label className="text-xs">Email</Label>
                <Input className="mt-1" value={f.new_email} onChange={set("new_email")} data-testid="import-new-email" /></div>
              <div><Label className="text-xs">Adresse</Label>
                <Input className="mt-1" value={f.new_address} onChange={set("new_address")} data-testid="import-new-address" /></div>
              <div><Label className="text-xs">Code postal</Label>
                <Input className="mt-1" value={f.new_zip} onChange={set("new_zip")} data-testid="import-new-zip" /></div>
              <div><Label className="text-xs">Ville</Label>
                <Input className="mt-1" value={f.new_city} onChange={set("new_city")} data-testid="import-new-city" /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">N° du devis original</Label>
              <Input className="mt-1" value={f.original_number} onChange={set("original_number")} placeholder="Ex : EXT-2024-118" data-testid="import-original-number" /></div>
            <div><Label className="text-xs">Titre du devis</Label>
              <Input className="mt-1" value={f.title} onChange={set("title")} placeholder="Ex : Refonte site web" data-testid="import-title" /></div>
            <div><Label className="text-xs">Date d'émission</Label>
              <Input type="date" className="mt-1" value={f.issue_date} onChange={set("issue_date")} data-testid="import-issue-date" /></div>
            <div><Label className="text-xs">Date de validation</Label>
              <Input type="date" className="mt-1" value={f.validated_date} onChange={set("validated_date")} data-testid="import-validated-date" /></div>
            <div><Label className="text-xs">Montant HT (€)</Label>
              <Input type="number" step="0.01" className="mt-1" value={f.total_ht} onChange={set("total_ht")} data-testid="import-ht" /></div>
            <div><Label className="text-xs">TVA (€)</Label>
              <Input type="number" step="0.01" className="mt-1" value={f.total_tva} onChange={set("total_tva")} data-testid="import-tva" /></div>
            <div><Label className="text-xs">Montant TTC (€) *</Label>
              <Input type="number" step="0.01" className="mt-1" value={f.total_ttc} onChange={set("total_ttc")} data-testid="import-ttc" /></div>
            <div><Label className="text-xs">Mode de validation</Label>
              <select className={`${sel} mt-1`} value={f.validation_mode} onChange={set("validation_mode")} data-testid="import-validation-mode">
                <option value="email">E-mail</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="signature_papier">Signature papier</option>
                <option value="autre">Autre</option>
              </select></div>
            <div className="col-span-2"><Label className="text-xs">Statut</Label>
              <select className={`${sel} mt-1`} value={f.status} onChange={set("status")} data-testid="import-status">
                <option value="brouillon">Brouillon</option>
                <option value="envoye">Envoyé</option>
                <option value="accepte">Accepté</option>
                <option value="refuse">Refusé</option>
                <option value="annule">Annulé</option>
              </select></div>
            <div className="col-span-2"><Label className="text-xs">Devis original (PDF) *</Label>
              <Input type="file" accept="application/pdf" className="mt-1" onChange={(e) => setPdf(e.target.files[0] || null)} data-testid="import-pdf" /></div>
          </div>
          <p className="text-[11px] text-neutral-400">Un devis importé « Accepté » est comptabilisé dans le CA prévisionnel (devis acceptés à facturer), distinct du CA facturé. Vous pourrez créer la facture directement depuis le devis.</p>
          <Button onClick={submit} disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="import-submit">
            {busy ? "Import…" : "Importer le devis"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
